<template>
  <div class="player_duplicates">
    <page-header :title="$t('player.duplicates')">
    </page-header>
    <page-content>
      <div class="row justify-content-center">
        <div class="col-xl-8 col-lg-10">
          <div v-if="loading" class="text-center p-5">
            <i class="fa fa-cog fa-spin fa-3x mb-3"></i>
            <h5>{{ $t('player.duplicatesLoadingMessage') }}</h5>
          </div>
          <div v-else-if="duplicates.length === 0" class="text-center p-5">
            <h4>{{ $t('noData') }}</h4>
          </div>
          <div v-else>
            <div v-for="(pair, index) in duplicates" :key="index" class="pair-container mb-4">
              <ntm-block>
                <div class="row align-items-center">
                  <div class="col-md-5">
                    <div class="player-info p-3 border rounded" :class="{'bg-success-light border-success': selectedOriginals[index] === 'playerA'}">
                      <div class="d-flex align-items-center">
                        <img v-if="pair.playerA.profile" :src="pair.playerA.profile" class="rounded-circle mr-3" style="width: 50px; height: 50px; object-fit: cover;">
                        <div v-else class="bg-body-light d-flex justify-content-center align-items-center rounded-circle mr-3" style="width: 50px; height: 50px;">
                          <i class="fa fa-user text-muted"></i>
                        </div>
                        <div>
                          <h5 class="mb-0">{{ pair.playerA.name }}</h5>
                          <div class="d-flex flex-column">
                            <small class="text-muted">{{ pair.playerA.position }}</small>
                            <small v-if="pair.playerA.lastTeam" class="text-primary font-weight-bold">{{ pair.playerA.lastTeam }}</small>
                          </div>
                        </div>
                      </div>
                      <div v-if="mergingIndex === index" class="mt-2">
                        <button class="btn btn-sm btn-block" :class="selectedOriginals[index] === 'playerA' ? 'btn-success' : 'btn-outline-success'" @click="selectOriginal(index, 'playerA')">
                          <i class="fa" :class="selectedOriginals[index] === 'playerA' ? 'fa-check-circle' : 'fa-circle-o'"></i> Original
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-2 text-center">
                    <div class="v-divider d-none d-md-block" style="height: 50px; border-left: 1px solid #eee; margin: 0 auto; width: 1px;"></div>
                    <div class="my-2 text-muted font-weight-bold">VS</div>
                    <div class="v-divider d-none d-md-block" style="height: 50px; border-left: 1px solid #eee; margin: 0 auto; width: 1px;"></div>
                  </div>
                  <div class="col-md-5">
                    <div class="player-info p-3 border rounded" :class="{'bg-success-light border-success': selectedOriginals[index] === 'playerB'}">
                      <div class="d-flex align-items-center">
                        <img v-if="pair.playerB.profile" :src="pair.playerB.profile" class="rounded-circle mr-3" style="width: 50px; height: 50px; object-fit: cover;">
                        <div v-else class="bg-body-light d-flex justify-content-center align-items-center rounded-circle mr-3" style="width: 50px; height: 50px;">
                          <i class="fa fa-user text-muted"></i>
                        </div>
                        <div>
                          <h5 class="mb-0">{{ pair.playerB.name }}</h5>
                          <div class="d-flex flex-column">
                            <small class="text-muted">{{ pair.playerB.position }}</small>
                            <small v-if="pair.playerB.lastTeam" class="text-primary font-weight-bold">{{ pair.playerB.lastTeam }}</small>
                          </div>
                        </div>
                      </div>
                      <div v-if="mergingIndex === index" class="mt-2">
                        <button class="btn btn-sm btn-block" :class="selectedOriginals[index] === 'playerB' ? 'btn-success' : 'btn-outline-success'" @click="selectOriginal(index, 'playerB')">
                          <i class="fa" :class="selectedOriginals[index] === 'playerB' ? 'fa-check-circle' : 'fa-circle-o'"></i> Original
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row mt-3 border-top pt-3 mb-3">
                  <div class="col-12 text-right">
                    <template v-if="mergingIndex !== index">
                      <button class="btn btn-sm btn-warning mr-2" @click="startMerge(index)">
                        <i class="fa fa-code-fork"></i> {{ $t('player.merge') }}
                      </button>
                      <button class="btn btn-sm btn-secondary" :disabled="actionLoading" @click="ignoreDuplicate(index)">
                        <i v-if="actionLoading && mergingIndex === null" class="fa fa-cog fa-spin"></i>
                        <i v-else class="fa fa-eye-slash"></i> {{ $t('action.ignore') }}
                      </button>
                    </template>
                    <template v-else>
                      <button class="btn btn-sm btn-primary mr-2" :disabled="!selectedOriginals[index] || actionLoading" @click="confirmMerge(index)">
                        <i v-if="actionLoading" class="fa fa-cog fa-spin"></i> {{ $t('action.save') }}
                      </button>
                      <button class="btn btn-sm btn-secondary" :disabled="actionLoading" @click="cancelMerge()">
                        {{ $t('action.cancel') }}
                      </button>
                    </template>
                  </div>
                </div>
              </ntm-block>
            </div>

            <ul v-if="model.meta && model.meta.last_page > 1" class="pagination mt-4">
              <li class="page-item" :class="{disabled: model.meta.current_page === 1}">
                <a class="page-link" @click="setPage(1)" href="javascript:void(0)" aria-label="First">
                  <span aria-hidden="true">&laquo;&laquo;</span>
                </a>
              </li>
              <li class="page-item" :class="{disabled: model.meta.current_page === 1}">
                <a class="page-link" @click="setPage(model.meta.current_page - 1)" href="javascript:void(0)" aria-label="Previous">
                  <span aria-hidden="true">&laquo;</span>
                </a>
              </li>

              <li v-for="p in pages" :key="'page-' + p" class="page-item" :class="{active: model.meta.current_page === p, disabled: p === '...'}">
                <a @click="p !== '...' && setPage(p)" class="page-link" href="javascript:void(0)">{{ p }}</a>
              </li>

              <li class="page-item" :class="{disabled: model.meta.current_page === model.meta.last_page}">
                <a class="page-link" @click="setPage(model.meta.current_page + 1)" href="javascript:void(0)" aria-label="Next">
                  <span aria-hidden="true">&raquo;</span>
                </a>
              </li>
              <li class="page-item" :class="{disabled: model.meta.current_page === model.meta.last_page}">
                <a class="page-link" @click="setPage(model.meta.last_page)" href="javascript:void(0)" aria-label="Last">
                  <span aria-hidden="true">&raquo;&raquo;</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </page-content>
  </div>
</template>

<script>
import PlayerService from '@/services/player.service'
import Swal from 'sweetalert2'
import VueI18n from '@/i18n'
import basicIndexMixin from '@/mixins/basic.index.mixin'

export default {
  mixins: [basicIndexMixin],
  data () {
    return {
      actionLoading: false,
      duplicates: [],
      mergingIndex: null,
      selectedOriginals: {}
    }
  },
  computed: {
    pages () {
      if (!this.model.meta) return []
      const current = this.model.meta.current_page
      const last = this.model.meta.last_page
      const range = 2
      const left = current - range
      const right = current + range + 1
      const rangeArray = []
      const rangeWithDots = []
      let l

      for (let i = 1; i <= last; i++) {
        if (i === 1 || i === last || (i >= left && i < right)) {
          rangeArray.push(i)
        }
      }

      for (const i of rangeArray) {
        if (l) {
          if (i - l === 2) {
            rangeWithDots.push(l + 1)
          } else if (i - l !== 1) {
            rangeWithDots.push('...')
          }
        }
        rangeWithDots.push(i)
        l = i
      }

      return rangeWithDots
    }
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    fetchData () {
      this.loading = true
      PlayerService.duplicates({ page: this.page }).then(response => {
        this.setData(response.data)
        this.duplicates = response.data.data
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    fetchDuplicates () {
      this.fetchData()
    },
    startMerge (index) {
      this.mergingIndex = index
      this.$set(this.selectedOriginals, index, null)
    },
    cancelMerge () {
      this.mergingIndex = null
    },
    selectOriginal (index, type) {
      this.$set(this.selectedOriginals, index, type)
    },
    ignoreDuplicate (index) {
      const pair = this.duplicates[index]

      Swal.fire({
        title: VueI18n.t('swal.ignorePlayerDuplicate.title') || 'Are you sure?',
        text: VueI18n.t('swal.ignorePlayerDuplicate.text') || 'These players will no longer be shown as duplicates.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: VueI18n.t('swal.ignorePlayerDuplicate.yes') || 'Yes, ignore!',
        cancelButtonText: VueI18n.t('swal.ignorePlayerDuplicate.no') || 'No, cancel'
      }).then((result) => {
        if (result.value) {
          this.actionLoading = true
          PlayerService.ignoreDuplicate({
            playerAUuid: pair.playerA.uuid,
            playerBUuid: pair.playerB.uuid
          }).then((res) => {
            this.$notify(res.data, 'success')
            this.duplicates.splice(index, 1)
            this.actionLoading = false
          }).catch(() => {
            this.actionLoading = false
          })
        }
      })
    },
    confirmMerge (index) {
      const pair = this.duplicates[index]
      const selectedType = this.selectedOriginals[index]

      let original, copy
      if (selectedType === 'playerA') {
        original = pair.playerA
        copy = pair.playerB
      } else {
        original = pair.playerB
        copy = pair.playerA
      }

      Swal.fire({
        title: VueI18n.t('swal.mergePlayers.title'),
        text: VueI18n.t('swal.mergePlayers.text'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: VueI18n.t('swal.mergePlayers.yes'),
        cancelButtonText: VueI18n.t('swal.mergePlayers.no')
      }).then((result) => {
        if (result.value) {
          this.actionLoading = true
          PlayerService.merge({
            original: original,
            copy: copy
          }).then((res) => {
            this.$notify(res.data, 'success')
            this.duplicates.splice(index, 1)
            this.mergingIndex = null
            this.actionLoading = false
          }).catch(() => {
            this.actionLoading = false
          })
        }
      })
    }
  }
}
</script>

<style scoped>
.bg-success-light {
  background-color: #e8f5e9;
}
.player-info {
  transition: all 0.3s ease;
}
</style>
